import React from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';

/**
 * Exported so that any call site marking stream items read can refresh the bell
 * via `refetchQueries`. Prefer `useMarkNotificationStreamItemsRead` below, which
 * already does this.
 */
export const NOTIFICATIONS_BELL_COUNT_QUERY = gql(`
  query NotificationsBellCount {
    notificationTasks {
      tasks {
        unreadCount
      }
    }
    notificationStream {
      totalUnread
    }
    notificationActions {
      actions {
        key
      }
    }
  }
`);

export const MARK_NOTIFICATION_STREAM_ITEMS_READ = gql(`
  mutation NotificationsCenterMarkStreamItemsRead($input: MarkNotificationStreamItemsReadInput!) {
    markNotificationStreamItemsRead(input: $input) {
      count
      readAt
    }
  }
`);

export interface NotificationsBellCount {
  /**
   * Total badge count for the bell: unread task counts, plus the stream's
   * unread total, plus one per pending notification action. `0` while loading
   * or on error.
   */
  count: number;
  loading: boolean;
  /** Re-reads the count. Safe to call from event handlers; never rejects. */
  refetch: () => void;
}

/**
 * Owns the treatment bell's badge count, read from CoreQL.
 *
 * Only mount this on the treatment branch of the notifications-center
 * experiment — control users continue to read their count from `/my_counts`,
 * and mounting this hook for them would issue a `NotificationsBellCount`
 * request they never use.
 *
 * The count refreshes when the tab regains visibility, and after
 * `useMarkNotificationStreamItemsRead` completes.
 */
export function useNotificationsBellCount(): NotificationsBellCount {
  const { data, loading, refetch } = useQuery(NOTIFICATIONS_BELL_COUNT_QUERY);

  // Apollo's refetch rejects on network failure. A stale bell count is not
  // worth an unhandled rejection, so swallow it and wait for the next refresh.
  const refetchQuietly = React.useCallback(() => {
    refetch().catch(() => {});
  }, [refetch]);

  React.useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        refetchQuietly();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchQuietly]);

  // Fields can come back null on a partial success, so every hop is guarded.
  const taskUnreadCount = (data?.notificationTasks?.tasks ?? [])
    .reduce((total, task) => total + (task?.unreadCount ?? 0), 0);
  const streamUnreadCount = data?.notificationStream?.totalUnread ?? 0;
  // NotificationAction carries no unread count, so each pending action counts once.
  const actionCount = (data?.notificationActions?.actions ?? []).length;

  return {
    count: taskUnreadCount + streamUnreadCount + actionCount,
    loading,
    refetch: refetchQuietly,
  };
}

/**
 * Marks stream items read and refreshes the bell count. Use this rather than
 * calling the mutation directly, so the two never drift apart.
 */
export function useMarkNotificationStreamItemsRead() {
  return useMutation(MARK_NOTIFICATION_STREAM_ITEMS_READ, {
    refetchQueries: [{ query: NOTIFICATIONS_BELL_COUNT_QUERY }],
  });
}
